// Models
import { FormFieldSchema, FormFieldArraySchema } from 'app/shared/models/form';
import { U21FormFieldTypes } from 'app/shared/u21-ui/components';

// Utils
import { required } from 'app/shared/utils/validation';

// Constants
import { COUNTRY_CODE_OPTIONS } from 'app/modules/goAML/schemas/catalogues/countryCode';
import { LEGAL_FORM_TYPE_OPTIONS } from 'app/modules/goAML/schemas/catalogues/legalFormType';
import { T_PHONE_SCHEMA } from 'app/modules/goAML/schemas/tPhoneSchema';
import { T_ADDRESS_SCHEMA } from 'app/modules/goAML/schemas/tAddressSchema';
import { DIRECTOR_ID_MY_CLIENT_SCHEMA } from 'app/modules/goAML/schemas/directorIDSchema';

export const ENTITY_NAME: FormFieldSchema<string> = {
  label: 'Name',
  name: 'name',
  type: U21FormFieldTypes.TEXT,
  required: true,
  validate: required('Entity name is required.'),
};

export const COMMERCIAL_NAME: FormFieldSchema<string> = {
  label: 'Commercial name',
  name: 'commercial_name',
  type: U21FormFieldTypes.TEXT,
};

export const LEGAL_TYPE: FormFieldSchema<string> = {
  label: 'Incorporation legal form type',
  name: 'incorporation_legal_form',
  type: U21FormFieldTypes.SELECT,
  fieldProps: {
    options: LEGAL_FORM_TYPE_OPTIONS,
  },
  required: true,
  validate: required('Incorporation legal form type is required.'),
};

export const INCORPORATION_NUMBER: FormFieldSchema<string> = {
  label: 'Incorporation number',
  name: 'incorporation_number',
  type: U21FormFieldTypes.TEXT,
};

export const ENTITY_BUSINESS: FormFieldSchema<string> = {
  label: 'Type of business',
  name: 'business',
  type: U21FormFieldTypes.TEXT,
};

export const EMAIL_FIELD: FormFieldSchema<string> = {
  label: 'Email',
  name: 'email',
  type: U21FormFieldTypes.TEXT,
};

export const URL_FIELD: FormFieldSchema<string> = {
  label: 'URL',
  name: 'url',
  type: U21FormFieldTypes.TEXT,
};

export const INC_STATE_FIELD: FormFieldSchema<string> = {
  label: 'Incorporation state',
  name: 'incorporation_state',
  type: U21FormFieldTypes.TEXT,
};

export const INC_COUNTRY_FIELD: FormFieldSchema<string> = {
  label: 'Incorporation country',
  name: 'incorporation_country_code',
  type: U21FormFieldTypes.SELECT,
  fieldProps: {
    options: COUNTRY_CODE_OPTIONS,
  },
};

export const INC_DATE_FIELD: FormFieldSchema<string> = {
  label: 'Incorporation date',
  name: 'incorporation_date',
  type: U21FormFieldTypes.DATE_PICKER,
};

export const IS_CLOSED_FIELD: FormFieldSchema<string> = {
  label: 'Business closed?',
  name: 'business_closed',
  type: U21FormFieldTypes.SELECT,
  fieldProps: {
    options: [
      {
        value: '0',
        text: 'No',
      },
      {
        value: '1',
        text: 'Yes',
      },
    ],
  },
};

export const DATE_BUSINESS_CLOSED_FIELD: FormFieldSchema<string> = {
  label: 'Date business closed',
  name: 'date_business_closed',
  type: U21FormFieldTypes.DATE_PICKER,
};

export const TAX_NUMBER_FIELD: FormFieldSchema<string> = {
  label: 'Tax number',
  name: 'tax_number',
  type: U21FormFieldTypes.TEXT,
};

export const TAX_REG_NUMBER_FIELD: FormFieldSchema<string> = {
  label: 'Tax registration number',
  name: 'tax_reg_number',
  type: U21FormFieldTypes.TEXT,
};

export const COMMENTS_FIELD: FormFieldSchema<string> = {
  label: 'Comments',
  name: 'comments',
  type: U21FormFieldTypes.TEXTAREA,
};

export const PHONES_FIELD: FormFieldArraySchema<any> = {
  name: 'phones',
  label: 'Phones',
  fields: [
    ...T_PHONE_SCHEMA.map((config) => ({
      ...config,
      name: `phone.${config.name}`,
    })),
  ],
  type: 'ARRAY',
};

export const ADDRESSES_FIELD: FormFieldArraySchema<any> = {
  name: 'addresses',
  label: 'Addresses',
  fields: [
    ...T_ADDRESS_SCHEMA.map((config) => ({
      ...config,
      name: `address.${config.name}`,
    })),
  ],
  type: 'ARRAY',
};

export const T_ENTITY_NESTED_INPUTS = [PHONES_FIELD, ADDRESSES_FIELD];

export const T_ENTITY_RAW_INPUTS_SCHEMA = [
  ENTITY_NAME,
  COMMERCIAL_NAME,
  LEGAL_TYPE,
  INCORPORATION_NUMBER,
  ENTITY_BUSINESS,
  EMAIL_FIELD,
  URL_FIELD,
  INC_STATE_FIELD,
  INC_COUNTRY_FIELD,
  INC_DATE_FIELD,
  IS_CLOSED_FIELD,
  DATE_BUSINESS_CLOSED_FIELD,
  TAX_NUMBER_FIELD,
  TAX_REG_NUMBER_FIELD,
  COMMENTS_FIELD,
];

export const T_ENTITY_MY_CLIENT_RAW_INPUTS_SCHEMA =
  T_ENTITY_RAW_INPUTS_SCHEMA.map((input) => {
    // My Client has the same fields but some are required
    switch (input.name) {
      case 'incorporation_number':
        return {
          ...input,
          required: true,
          validate: required('Incorporation number is required.'),
        };
      case 'business':
        return {
          ...input,
          required: true,
          validate: required('Type of business is required.'),
        };
      case 'incorporation_country_code':
        return {
          ...input,
          required: true,
          validate: required('Incorporation country is required.'),
        };
      case 'incorporation_date':
        return {
          ...input,
          required: true,
          validate: required('Incorporation date is required.'),
        };
      default:
        return input;
    }
  });

// Schemas
export const T_ENTITY_SCHEMA = [
  ...T_ENTITY_RAW_INPUTS_SCHEMA,
  ...T_ENTITY_NESTED_INPUTS,
];

export const T_ENTITY_MY_CLIENT_SCHEMA = [
  ...T_ENTITY_MY_CLIENT_RAW_INPUTS_SCHEMA,
  ...T_ENTITY_NESTED_INPUTS,
  ...DIRECTOR_ID_MY_CLIENT_SCHEMA.map((field) => ({
    ...field,
    name: `director_id.${field.name}`,
  })),
];
